import { defaultAllSelection } from "services/constants/selections";
import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import {
  CalendarTodayRounded,
  GppGoodRounded,
  Groups2Rounded,
  RefreshRounded,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  designColors,
  designRadii,
  designShell,
  designTheme,
  designType,
} from "services/constants/designTokens";
import { GlobalNoticeBanner } from "components/DesignSystem/sharedUiPatterns";
import { EmptyState } from "components/CustomComponents/EmptyState";
import { useFetch } from "services/hooks/useFetch";
import { sectionCardSx } from "./homeUi";
import { WorkforceSummaryCards } from "./WorkforceSummaryCards";
import { WorkforceStatusSection } from "./WorkforceStatusSection";
import { PendingApprovalsSection } from "./PendingApprovalsSection";
import { PendingReportsSection } from "./PendingReportsSection";
import { AttendanceExceptionsSection } from "./AttendanceExceptionsSection";
import { ShiftCoverageSection } from "./ShiftCoverageSection";
import { UpcomingLeaveSection } from "./UpcomingLeaveSection";
import { RosterPromoBanner } from "./RosterPromoBanner";
import { HomeAccessGate } from "./HomeAccessGate";
import { getHomeDashboardAccess } from "./homeDashboardAccess";

const moduleRouteMap = {
  Approvals: "/admin/teamzone/approve",
  Attendance: "/admin/teamzone/attendance",
  Rosters: "/admin/teamzone/plan-shift",
  "Leave Balances": "/admin/teamzone/leave-summary",
  Reports: "/admin/teamzone/report",
  "EOD Report": "/admin/teamzone/eodreport",
};

const TeamWorkspaceHomeContent = () => {
  const history = useHistory();
  const { userScreenAccess, domain, userDetails, topbarCompanyId } =
    useSelector((state) => state.tokenReducer);

  const { availableModules, canViewAttendanceOverview, canViewEmployeeAttendance, approvalAccess } =
    useMemo(() => getHomeDashboardAccess(userScreenAccess?.module_details), [userScreenAccess]);

  const hasModule = (moduleName) => availableModules.has(moduleName);

  // Every navigateToModule call also carries the Home date picker's current
  // selection (todayDate, declared below — still in scope here by closure
  // since this only runs once someone clicks through, by which point the
  // whole component has finished its first render) along as a `date` query
  // param, merged into whatever search string the caller already builds.
  // Otherwise a screen someone opens from Home after picking, say,
  // yesterday would silently reset to today instead of showing the same
  // day Home was showing. Attendance, Approve, and Shift each read this
  // `date` param on landing and apply it as their own initial date/range.
  const navigateToModule = (moduleName, search = "") => {
    const route = moduleRouteMap[moduleName];
    if (route && hasModule(moduleName)) {
      const params = new URLSearchParams(search);
      params.set("date", todayDate);
      history.push({
        pathname: route,
        search: `?${params.toString()}`,
      });
    }
  };

  const visibleModuleCount = availableModules.size;
  // hasModule("Approvals") is true with viewer access to ANY of the 11
  // approval screens (see homeDashboardAccess.js) — broader than
  // approvalAccess's own leave/overtime/permission/all keys, which only
  // cover the screens this page breaks into their own row. Using the
  // narrower check here left Attendance/Rosters/etc. fetching fine but
  // meant someone with, say, only Comp Off access (no Leave/Overtime/
  // Permission, and not "all") got no snapshot at all — even though
  // PendingApprovalsSection's All/Other Pending Approvals rows don't
  // require any specific one of those and would otherwise have something
  // to show them.
  const needsAttendanceCounts = ["Attendance", "Rosters", "Leave Balances", "EOD Report"]
    .some(hasModule) || hasModule("Approvals");
  const asOfLabel = dayjs().format("ddd, DD MMM YYYY hh:mm A");

  // Home used to be pinned to "today" everywhere it fetched a snapshot;
  // selectedDate makes that a user choice (see the date picker below) while
  // still defaulting to today on first load. Deliberately NOT persisted
  // (unlike the Approval Center's own date-range filter, which is) — Home
  // is meant to read as "as of right now" whenever you land on it, so a
  // fresh visit always starts at today even if a previous visit in this
  // same tab had picked a different day. A day picked here still carries
  // through to whichever screen you navigate to next via navigateToModule's
  // `date` param below; it just doesn't carry back once you return to Home
  // itself.
  const [selectedDate, setSelectedDate] = useState(() => dayjs());
  const isSelectedDateToday = selectedDate.isSame(dayjs(), "day");
  // Short form for the stat-card titles below ("Present Today" / "Present 12
  // Aug") and the longer parenthesised form for section headers
  // ("Attendance Exceptions (Today)" / "... (12 Aug 2026)").
  const dayLabel = isSelectedDateToday
    ? "Today"
    : selectedDate.format("DD MMM");
  const dateSuffix = isSelectedDateToday
    ? "Today"
    : selectedDate.format("DD MMM YYYY");
  const todayDate = selectedDate.format("YYYY-MM-DD");

  // Same "overall attendance count" endpoint the Attendance module's status
  // screen uses (TeamZone/Attendance/AttendanceStatus/index.js) — shift/
  // department are left at "99999" (all) since Home has no filters of its
  // own beyond the date, just a whole-team snapshot for the picked day.
  const {
    result: attendanceCounts,
    setLoad: reloadAttendanceCounts,
    loading: snapshotLoading,
  } = useFetch(
    needsAttendanceCounts
      ? `${domain}get-overall-attendance-count/?shift_id=${defaultAllSelection}&date=${todayDate}&department_id=${defaultAllSelection}&user_id=${userDetails?.id}&status=total&substatus=&company_id=${topbarCompanyId}`
      : null,
  );
  const attendanceCountsLoading = needsAttendanceCounts && snapshotLoading;

  // Same per-category pending/approved/rejected/cancelled endpoint the
  // Approval Center itself uses (TeamZone/Approve/index.js) — fetched here
  // too (scoped to just today, matching the rest of Home's snapshot) purely
  // to power PendingApprovalsSection's Permission Requests row and its
  // "View all" breakdown panel, without waiting on someone to open the
  // Approval Center first.
  const needsApprovalCategoryCounts = hasModule("Approvals");
  const {
    result: approvalCategoryCounts,
    setLoad: reloadApprovalCategoryCounts,
  } = useFetch(
    needsApprovalCategoryCounts
      ? `${domain}team_zone/overall_count/approval-counts/?user_id=${userDetails?.id}&start_date=${todayDate}&end_date=${todayDate}&company_id=${topbarCompanyId}`
      : null,
  );

  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  // UpcomingLeaveSection and PendingReportsSection each run their own fetch
  // independent of attendanceCounts (leave calendar + company holidays, and
  // eod-reports respectively) — reloadAttendanceCounts(true) alone never
  // touches them, so the Refresh button looked like it did nothing for
  // those two cards. Bumping this and passing it down as a prop gives both
  // a dependency to key their own refetch off.
  const [refreshKey, setRefreshKey] = useState(0);

  const handleDateChange = (newValue) => {
    if (newValue && newValue.isValid() && !newValue.isAfter(dayjs(), "day")) {
      setSelectedDate(newValue);
      // useFetch only refetches when `load` flips true (see its effect
      // deps) — changing the url alone wouldn't trigger a new request.
      reloadAttendanceCounts(true);
      reloadApprovalCategoryCounts(true);
    }
    setDatePickerAnchor(null);
  };

  return (
    <ThemeProvider theme={designTheme}>
      <Mui.Box
        sx={{
          ...designShell.pageSurface,
          py: { xs: 2, md: 2.5 },
          // MUI's Grid container widens itself to calc(100% + gutter) and
          // pulls back with a negative left margin to build its row gutters
          // (only left, never right) — so it always bleeds past its parent's
          // edges by one gutter's worth of pixels (12px for the
          // spacing={1.5} rows below). Horizontal padding here must be >=
          // that 12px on every breakpoint, or the bleed either creeps past
          // the left edge (misaligning rows 2/3 against the title and stat
          // cards above them, which aren't Grids) or the right edge (a real
          // horizontal scrollbar on the whole app shell, since Sidebar's
          // <main> wrapper has no overflow-x guard).
          px: { xs: 2, md: 3 },
          overflowX: "hidden",
        }}
      >
        <Mui.Stack spacing={2.5}>
          {!attendanceCountsLoading && (
            <Mui.Stack
              direction={{ xs: "column", lg: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", lg: "center" }}
              spacing={2}
            >
              <Mui.Box>
                <Mui.Stack direction="row" alignItems="center" spacing={1.5}>
                  <Mui.Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${designColors.tealTint} 0%, #F4FBF8 100%)`,
                      color: designColors.tealDark,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Groups2Rounded sx={{ fontSize: 32 }} />
                  </Mui.Box>
                  <Mui.Box>
                    <Mui.Typography sx={designType.pageTitle}>
                      Team Workspace Home
                    </Mui.Typography>
                    <Mui.Typography sx={designType.pageSubtitle}>
                      Overview of your team’s status and items requiring your
                      attention.
                    </Mui.Typography>
                  </Mui.Box>
                </Mui.Stack>
              </Mui.Box>
              <Mui.Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Mui.Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.6}
                  onClick={(event) => setDatePickerAnchor(event.currentTarget)}
                  sx={{
                    cursor: "pointer",
                    px: 1,
                    py: 0.5,
                    borderRadius: designRadii.control,
                    border: `1px solid transparent`,
                    "&:hover": {
                      backgroundColor: designColors.slateBg,
                      border: `1px solid ${designColors.slate200}`,
                    },
                  }}
                >
                  <CalendarTodayRounded
                    sx={{ fontSize: 15, color: designColors.tealDark }}
                  />
                  <Mui.Typography
                    sx={{
                      fontSize: 13.5,
                      color: designColors.slate700,
                      fontWeight: 600,
                    }}
                  >
                    {isSelectedDateToday
                      ? `Data as of: ${asOfLabel}`
                      : `Showing data for: ${selectedDate.format("ddd, DD MMM YYYY")}`}
                  </Mui.Typography>
                </Mui.Stack>
                <Mui.Popover
                  open={Boolean(datePickerAnchor)}
                  anchorEl={datePickerAnchor}
                  onClose={() => setDatePickerAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <StaticDatePicker
                      value={selectedDate}
                      onChange={handleDateChange}
                      disableFuture
                      displayStaticWrapperAs="desktop"
                      slotProps={{ actionBar: { actions: [] } }}
                    />
                  </LocalizationProvider>
                </Mui.Popover>
                <Mui.Button
                  startIcon={<RefreshRounded />}
                  sx={{ ...designShell.compactOutlineButton, px: 1.75 }}
                  onClick={() => {
                    reloadAttendanceCounts(true);
                    reloadApprovalCategoryCounts(true);
                    setRefreshKey((key) => key + 1);
                  }}
                >
                  Refresh
                </Mui.Button>
              </Mui.Stack>
            </Mui.Stack>
          )}

          <WorkforceSummaryCards
            availableModules={availableModules}
            canViewEmployeeAttendance={canViewEmployeeAttendance}
            approvalAccess={approvalAccess}
            attendanceCounts={attendanceCounts}
            approvalCategoryCounts={approvalCategoryCounts}
            dayLabel={dayLabel}
            loading={attendanceCountsLoading}
            navigateToModule={navigateToModule}
          />

          {canViewAttendanceOverview && <GlobalNoticeBanner
            icon={
              attendanceCountsLoading ? null : (
                <GppGoodRounded fontSize="small" sx={{ color: "#15803D" }} />
              )
            }
            sx={{
              py: 1,
              px: 1.75,
              backgroundColor: "#F7FDF9",
              border: "1px solid #CBE7D5",
              alignItems: "center",
              "& > .MuiTypography-root": {
                pt: 0,
                minWidth: 0,
              },
            }}
          >
            {attendanceCountsLoading ? (
              <Mui.Skeleton
                variant="text"
                width={320}
                height={22}
                sx={{ transform: "none" }}
              />
            ) : (
              <>
                All workforce status counts are accurately reconciled with
                the total of {attendanceCounts?.overall_count ?? 0} team
                members.
              </>
            )}
          </GlobalNoticeBanner>}

          {visibleModuleCount === 0 ? (
            <Mui.Box sx={{ ...sectionCardSx, py: 2 }}>
              <EmptyState
                title="No Team Workspace modules available"
                subtitle="Home sections will appear here when Team Zone module access is provisioned."
              />
            </Mui.Box>
          ) : (
            <>
              {/* Plain flexbox, not Grid — each card's module flag
                  (hasModule(...)) can hide it independently of its row
                  siblings, and a fixed-fraction Grid (xl={3} each, assuming
                  all 4 render) leaves a blank gap where a hidden card used
                  to be instead of letting the rest fill the row. `flex: "1 1
                  0"` at xl+ divides the row evenly among however many cards
                  actually rendered — same technique WorkforceSummaryCards
                  above already uses for its module-gated stat cards. */}
              <Mui.Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                {hasModule("Attendance") ? (
                  <Mui.Box
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 calc(50% - 10px)",
                      },
                      "@media (min-width:1800px)": { flex: "1 1 0" },
                      minWidth: 0,
                    }}
                  >
                    <WorkforceStatusSection
                      navigateToModule={navigateToModule}
                      attendanceCounts={attendanceCounts}
                      loading={attendanceCountsLoading}
                      dateSuffix={dateSuffix}
                    />
                  </Mui.Box>
                ) : null}

                {hasModule("Approvals") ? (
                  <Mui.Box
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 calc(50% - 10px)",
                      },
                      "@media (min-width:1800px)": { flex: "1 1 0" },
                      minWidth: 0,
                    }}
                  >
                    <PendingApprovalsSection
                      approvalAccess={approvalAccess}
                      navigateToModule={navigateToModule}
                      attendanceCounts={attendanceCounts}
                      approvalCategoryCounts={approvalCategoryCounts}
                      loading={attendanceCountsLoading}
                    />
                  </Mui.Box>
                ) : null}

                {hasModule("EOD Report") ? (
                  <Mui.Box
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 calc(50% - 10px)",
                      },
                      "@media (min-width:1800px)": { flex: "1 1 0" },
                      minWidth: 0,
                    }}
                  >
                    <PendingReportsSection
                      navigateToModule={navigateToModule}
                      loading={attendanceCountsLoading}
                      todayDate={todayDate}
                      refreshKey={refreshKey}
                    />
                  </Mui.Box>
                ) : null}

                {canViewEmployeeAttendance ? (
                  <Mui.Box
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 calc(50% - 10px)",
                      },
                      "@media (min-width:1800px)": { flex: "1 1 0" },
                      minWidth: 0,
                    }}
                  >
                    <AttendanceExceptionsSection
                      navigateToModule={navigateToModule}
                      attendanceCounts={attendanceCounts}
                      dateSuffix={dateSuffix}
                      loading={attendanceCountsLoading}
                    />
                  </Mui.Box>
                ) : null}
              </Mui.Box>

              {/* Same reasoning as the row above: flex-grow ratios 7:5
                  reproduce the original 58%/42% split when both cards are
                  visible, but if only one of Shift/Leave Summary access is
                  granted, that lone card has no sibling to share the row
                  with and naturally fills 100% instead of stopping at its
                  fixed share. */}
              <Mui.Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                {hasModule("Rosters") ? (
                  <Mui.Box
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        md: "1 1 calc(60% - 10px)",
                        xl: "7 1 0",
                      },
                      minWidth: 0,
                    }}
                  >
                    <ShiftCoverageSection
                      navigateToModule={navigateToModule}
                      attendanceCounts={attendanceCounts}
                      dayLabel={dayLabel}
                      dateSuffix={dateSuffix}
                      loading={attendanceCountsLoading}
                    />
                  </Mui.Box>
                ) : null}

                {hasModule("Leave Balances") ? (
                  <Mui.Box
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        md: "1 1 calc(40% - 10px)",
                        xl: "5 1 0",
                      },
                      minWidth: 0,
                    }}
                  >
                    <UpcomingLeaveSection
                      navigateToModule={navigateToModule}
                      loading={attendanceCountsLoading}
                      refreshKey={refreshKey}
                    />
                  </Mui.Box>
                ) : null}
              </Mui.Box>

              {hasModule("Rosters") || hasModule("Reports") ? (
                <RosterPromoBanner
                  hasShiftModule={hasModule("Rosters")}
                  navigateToModule={navigateToModule}
                  loading={attendanceCountsLoading}
                />
              ) : null}
            </>
          )}
        </Mui.Stack>
      </Mui.Box>
    </ThemeProvider>
  );
};

const TeamWorkspaceHome = () => (
  <HomeAccessGate>
    <TeamWorkspaceHomeContent />
  </HomeAccessGate>
);

export { TeamWorkspaceHome };

export default TeamWorkspaceHome;
